import { API, graphqlOperation } from "aws-amplify";

import {
  listOutboundShipmentsForPlacementBackfill,
  outboundShipmentByStatusForPlacementBackfill,
} from "../../graphql/admin-queries";
import { gateCheckInByShipment } from "../../graphql/gatecheckin/custom-queries";
import { updateOutboundShipmentMini } from "../../graphql/outbound/custom-mutations";
import { isConditionalCheckFailed, logError } from "../../utils/appUtils";
import { OutboundStatus } from "../../utils/constants";
import {
  needsPlacementBackfill,
  PLACED_STATUSES,
} from "../../utils/placementBackfillRules";

// Selection rules live in utils/placementBackfillRules.js, shared with the
// operations script twin; re-exported for this module's tests and callers.
export { needsPlacementBackfill };

const PAGE_LIMIT = 200;
const WRITE_BATCH = 10;

/**
 * needsPlacementBackfill's gateCheckInId test alone is not safe for ARRIVED
 * rows: terminal check-ins null the shipment's gateCheckInId when the lock is
 * released (COMPLETED today; CANCELLED rows saved before the cancel rollback
 * existed), so a post-release gate arrival can present as ARRIVED with no
 * gateCheckInId — exactly what a legacy placement looks like. Only the driver
 * check-in API ever writes GateCheckIn rows, so any row for the shipment means
 * its ARRIVED came from the gate, and flipping it to PLACED would mark a
 * never-spotted load as placed and start its demurrage clock. A failed lookup
 * counts as a gate arrival: skipping a real legacy row is recoverable on the
 * next run, the false flip is not.
 */
const isGateArrival = async (record) => {
  try {
    const response = await API.graphql(
      graphqlOperation(gateCheckInByShipment, {
        shipmentKey: `out:${record.id}`,
        limit: 1,
      }),
    );
    return (response?.data?.gateCheckInByShipment?.items || []).length > 0;
  } catch (err) {
    logError(
      "AdminDashboard:backfillOutboundPlacementDateTime:gateCheckIn",
      err,
      false,
    );
    return true;
  }
};

/**
 * Copy OutboundShipment.arrivalDateTime into placementDateTime, and move
 * legacy ARRIVED rows to status PLACED.
 *
 * The dispatch tab's "Time Placed" input used to write arrivalDateTime; it now
 * writes placementDateTime, and arrivalDateTime carries the driver's gate
 * arrival. Status split the same way: PLACED now means placed, ARRIVED means
 * at the gate. Records saved before that change hold their placed time in
 * arrivalDateTime and their placed status as ARRIVED — until this runs they
 * show an empty "Time Placed", sit in the wrong OutboundList tab, and a
 * manager saving one would roll its status back to PENDING.
 *
 * arrivalDateTime is left in place: it is a sort-key component of the three
 * outbound by-dates GSIs, and for a legacy record "arrived" was the same event
 * as "placed".
 *
 * The status flip must ride the same AppSync update: status is part of the
 * by-dates and status#active composite @keys, so the input carries every
 * member of both keys (endDateTime, arrivalDateTime, scheduledDateTime,
 * createDateTime, active) and the update resolver recomposes the synthetic
 * sort attributes. LOADED/RELEASED rows keep their status and get the minimal
 * placement-only write.
 *
 * The browser twin of scripts/backfill-outbound-placement-time.js — same
 * selection rule and same per-record condition, over AppSync instead of a
 * direct DynamoDB scan. Dry run unless queryTerms.applyBackfill is true.
 *
 * @param {object} queryTerms - { reloadYardId?: string, applyBackfill?: boolean }
 *   reloadYardId empty/absent means every yard.
 * @param {function} notify - react-admin notify
 */
export const backfillOutboundPlacementDateTime = async (
  queryTerms = {},
  notify,
) => {
  const dryRun = queryTerms.applyBackfill !== true;
  const reloadYardId = queryTerms.reloadYardId || null;

  const filter = {
    arrivalDateTime: { attributeExists: true },
    // Absent for records written before placementDateTime was wired up; NULL
    // for ones a create wrote an explicit null into.
    or: [
      { placementDateTime: { attributeExists: false } },
      { placementDateTime: { attributeType: "_null" } },
    ],
  };

  let scanned = 0;
  const candidates = [];

  // Walks every page of one query; items are re-tested with
  // needsPlacementBackfill rather than trusted from the filter — the status
  // rule is the one that matters and a query filter is only a cheap prefilter.
  const collectPages = async (query, variables, extractPage) => {
    let nextToken = null;
    do {
      const response = await API.graphql(
        graphqlOperation(query, { ...variables, limit: PAGE_LIMIT, nextToken }),
      );
      const page = extractPage(response);
      const items = page?.items || [];
      scanned += items.length;
      candidates.push(...items.filter(needsPlacementBackfill));
      nextToken = page?.nextToken || null;
    } while (nextToken);
  };

  try {
    if (reloadYardId) {
      // With a yard picked, the outboundShipmentByStatus GSI reads exactly
      // the four placed-status partitions instead of scanning the table.
      for (const status of PLACED_STATUSES) {
        await collectPages(
          outboundShipmentByStatusForPlacementBackfill,
          { reloadYardId, status: { eq: status }, filter },
          (response) => response?.data?.outboundShipmentByStatus,
        );
      }
    } else {
      // All yards has no GSI hash key to query on; scan stays.
      await collectPages(
        listOutboundShipmentsForPlacementBackfill,
        { filter },
        (response) => response?.data?.listOutboundShipments,
      );
    }
  } catch (err) {
    logError("AdminDashboard:backfillOutboundPlacementDateTime:scan", err, notify);
    return null;
  }

  // Second-stage guard: only ARRIVED candidates get flipped to PLACED, so
  // each one is verified against the GateCheckIn table (see isGateArrival).
  const arrived = candidates.filter(
    (record) => record.status === OutboundStatus.ARRIVED,
  );
  const gateArrivalIds = new Set();
  for (let i = 0; i < arrived.length; i += WRITE_BATCH) {
    const batch = arrived.slice(i, i + WRITE_BATCH);
    const flags = await Promise.all(batch.map(isGateArrival));
    flags.forEach((isGate, index) => {
      if (isGate) gateArrivalIds.add(batch[index].id);
    });
  }
  const toBackfill = candidates.filter(
    (record) => !gateArrivalIds.has(record.id),
  );
  const gateArrivals = gateArrivalIds.size;

  console.log("[backfillOutboundPlacementDateTime] candidates", {
    reloadYardId: reloadYardId || "(all yards)",
    scanned,
    toBackfill: toBackfill.length,
    gateArrivals,
    dryRun,
  });
  console.table(
    toBackfill.slice(0, 50).map((record) => ({
      id: record.id,
      account: record.accountName,
      status: record.status,
      newStatus:
        record.status === OutboundStatus.ARRIVED
          ? OutboundStatus.PLACED
          : record.status,
      poNumber: record.poNumber,
      railCarNumber: record.railCarNumber,
      arrivalDateTime: record.arrivalDateTime,
    })),
  );

  if (dryRun) {
    if (notify)
      notify(
        `Dry run: ${toBackfill.length} of ${scanned} outbound shipments would be backfilled` +
          (gateArrivals ? ` (${gateArrivals} gate arrivals skipped)` : "") +
          ". See console.",
      );
    return {
      scanned,
      candidates: toBackfill.length,
      gateArrivals,
      dryRun: true,
    };
  }

  let copied = 0;
  let skipped = 0;
  let failed = 0;

  for (let i = 0; i < toBackfill.length; i += WRITE_BATCH) {
    const batch = toBackfill.slice(i, i + WRITE_BATCH);
    const results = await Promise.allSettled(
      batch.map((record) => {
        const flipToPlaced = record.status === OutboundStatus.ARRIVED;
        const input = flipToPlaced
          ? {
              id: record.id,
              placementDateTime: record.arrivalDateTime,
              status: OutboundStatus.PLACED,
              // Every other member of the two composite @keys status sits
              // in, echoed as scanned so the resolver can recompose their
              // synthetic sort attributes.
              active: record.active,
              endDateTime: record.endDateTime ?? null,
              arrivalDateTime: record.arrivalDateTime,
              scheduledDateTime: record.scheduledDateTime ?? null,
              createDateTime: record.createDateTime,
            }
          : { id: record.id, placementDateTime: record.arrivalDateTime };
        return API.graphql(
          graphqlOperation(updateOutboundShipmentMini, {
            input,
            // Conditioned on the fields still being as scanned, so a save
            // landing mid-run — a manager placing the load, a gate check-in
            // moving the arrival, or a status change — wins over this
            // backfill.
            condition: {
              arrivalDateTime: { eq: record.arrivalDateTime },
              status: { eq: record.status },
              or: [
                { placementDateTime: { attributeExists: false } },
                { placementDateTime: { attributeType: "_null" } },
              ],
            },
          }),
        );
      }),
    );

    const tally = results.reduce(
      (acc, result, index) => {
        if (result.status === "fulfilled") return { ...acc, ok: acc.ok + 1 };
        // Conditional failure means it changed underneath us: the live write
        // is newer, so leaving it alone is the right outcome, not an error.
        if (isConditionalCheckFailed(result.reason))
          return { ...acc, stale: acc.stale + 1 };
        logError(
          "AdminDashboard:backfillOutboundPlacementDateTime:update",
          result.reason,
          false,
        );
        console.error("failed", batch[index]?.id, result.reason);
        return { ...acc, bad: acc.bad + 1 };
      },
      { ok: 0, stale: 0, bad: 0 },
    );

    copied += tally.ok;
    skipped += tally.stale;
    failed += tally.bad;
  }

  const summary = {
    scanned,
    candidates: toBackfill.length,
    gateArrivals,
    copied,
    skipped,
    failed,
  };
  console.log("[backfillOutboundPlacementDateTime] done", summary);
  if (notify)
    notify(
      `Placement backfill: ${copied} copied, ${skipped} skipped, ${failed} failed.`,
      failed > 0 ? "warning" : "info",
    );
  return summary;
};
